import { Centrifuge } from 'centrifuge'
import { getValidToken, silentRefresh } from './authService'
import { apiRequest } from './apiClient'

// URL Centrifugo WebSocket — через nginx proxy в production, напрямую в dev.
// Chrome <124 не умеет относительный URL в конструкторе WebSocket — собираем абсолютный wss:// от текущего хоста
function resolveWsUrl(url) {
  if (/^wss?:\/\//i.test(url)) return url
  const proto = window.location.protocol === 'https:' ? 'wss://' : 'ws://'
  return proto + window.location.host + url
}
const WS_URL = resolveWsUrl(import.meta.env.VITE_CENTRIFUGO_WS_URL || '/centrifugo/connection/websocket')

let client = null

// Повертає валідний access токен, при потребі дочекавшись silent refresh
async function ensureAccessToken() {
  let token = getValidToken()
  if (!token) {
    await silentRefresh()
    token = getValidToken()
  }
  if (!token) throw new Error('not_authenticated')
  return token
}

/**
 * Синглтон Centrifuge-клиента для клиентского портала.
 * При первом вызове создаёт подключение с авто-получением токена через PHP API.
 */
export function getClient() {
  if (client) return client

  client = new Centrifuge(WS_URL, {
    getToken: async () => {
      await ensureAccessToken()
      // apiRequest сам додасть актуальний Authorization header
      const data = await apiRequest('/centrifugo/token')
      return data.token
    },
  })

  client.connect()
  return client
}

/**
 * Подписка на канал устройства device:{serial}.
 * Возвращает слушателя, которого снимают через removeSubscription.
 *
 * Идёт через общий реестр каналов: Centrifugo на вторую подписку к тому же каналу бросает
 * ошибку дубликата, а один канал на странице слушают сразу несколько мест — карточка
 * активного прибора на «Пристроях» и блок «АЗС» в попапе той же заправки. Раньше второй
 * из них молча оставался без живых данных: исключение ловил try вокруг вызова.
 */
export function subscribeToDevice(serial, handlers) {
  return subscribeShared(`device:${serial}`, handlers)
}

/**
 * Подписка на пользовательский канал user:{ownerUserId} (тревоги + ack-эхо).
 * Для суб-пользователей ownerUserId = parentUserId — канал общий на аккаунт владельца.
 */
// Канал user:{id} слушают несколько потребителей сразу: тревоги (useAlarmState)
// и задания (TasksPage). Centrifugo на вторую подписку к тому же каналу бросает
// ошибку дубликата, поэтому держим одну подписку и раздаём публикации всем.
// Ключ - канал; подписка снимается, когда уходит последний слушатель.
const userChannels = new Map()

export function subscribeToUserChannel(ownerUserId, handlers) {
  return subscribeShared(`user:${ownerUserId}`, handlers)
}

/**
 * Общая подписка на канал устройства device:{serial} для нескольких слушателей сразу
 * (два виджета «АЗС» одной заправки на дашборде). Снимается через removeSubscription.
 */
export function subscribeToDeviceShared(serial, handlers) {
  return subscribeShared(`device:${serial}`, handlers)
}

function subscribeShared(channel, { onPublication, onSubscribed, onError }) {
  let entry = userChannels.get(channel)

  if (!entry) {
    const c = getClient()
    const sub = c.newSubscription(channel, {
      getToken: async () => {
        await ensureAccessToken()
        const data = await apiRequest(`/centrifugo/subscribe-token?channel=${encodeURIComponent(channel)}`)
        return data.token
      },
    })
    entry = { sub, listeners: new Set() }
    // Публикацию отдаём каждому слушателю; падение одного не роняет остальных
    sub.on('publication', (ctx) => {
      for (const l of entry.listeners) {
        try {
          l.onPublication?.(ctx.data)
        } catch (e) { /* слушатель сам за себя */ }
      }
    })
    sub.on('subscribed', (ctx) => {
      for (const l of entry.listeners) {
        try {
          l.onSubscribed?.(ctx)
        } catch (e) { /* ignore */ }
      }
    })
    sub.on('error', (ctx) => {
      for (const l of entry.listeners) {
        try {
          l.onError?.(ctx)
        } catch (e) { /* ignore */ }
      }
    })
    userChannels.set(channel, entry)
    sub.subscribe()
  }

  const listener = { onPublication, onSubscribed, onError, __channel: channel }
  entry.listeners.add(listener)
  return listener
}

/**
 * Полностью удаляет подписку из клиента (unsubscribe + removeSubscription).
 * Без этого newSubscription на тот же канал бросит ошибку дубликата.
 */
export function removeSubscription(sub) {
  if (!sub) return

  // Слушатель общего канала (user:{id}, device:{serial}): снимаем только его, саму
  // подписку закрываем, когда ушёл последний
  if (sub.__channel) {
    const entry = userChannels.get(sub.__channel)
    if (!entry) return
    entry.listeners.delete(sub)
    if (entry.listeners.size > 0) return
    userChannels.delete(sub.__channel)
    if (!client) return
    try {
      entry.sub.unsubscribe()
      entry.sub.removeAllListeners()
      client.removeSubscription(entry.sub)
    } catch (e) { /* ignore */ }
    return
  }

  if (!client) return
  try {
    sub.unsubscribe()
    sub.removeAllListeners()
    client.removeSubscription(sub)
  } catch (e) { /* ignore */ }
}

// Слухачі скидання клієнта: довгоживучі підписники (useAlarmState) мають занулити
// свої subscription-об'єкти після logout, інакше після re-login триматимуть мертву підписку
const resetListeners = new Set()

export function onClientReset(listener) {
  resetListeners.add(listener)
}

/**
 * Отключает клиент Centrifugo (вызывать при logout).
 */
export function disconnectClient() {
  // Реестр общих каналов чистим вместе с клиентом: иначе после re-login
  // subscribeToUserChannel вернёт слушателя на мёртвую подписку
  userChannels.clear()
  if (client) {
    client.disconnect()
    client = null
  }
  for (const listener of resetListeners) {
    try { listener() } catch { /* ignore */ }
  }
}
